// =============================================================================
// UNDERGARMENT NARROW — managed both .is-nonexistent and .disabled for UG
// -----------------------------------------------------------------------------
// Scope (2026-06-16, revised): authoritative class manager for Undergarment
// fieldset, overriding native theme.js cross-narrowing because of a native
// bug surfaced by natalie-rolt's data shape.
//
//   .is-nonexistent — no variant exists with (current Color × Size × this UG)
//                     regardless of stock. Sets input.disabled=true + class on
//                     input+label. CSS (theme.dev.css) adds pointer-events:
//                     none + heavy fade. Shopper cannot select.
//
//   .disabled       — variant exists but variant.available === false (sold
//                     out). Native theme.css.liquid:8467 styles plain-text
//                     labels gray (#b1b1b1). For .undergarment-swatch (which
//                     also has .color-swatch class), native CSS skips — no
//                     visual cue, matching native Color swatch behavior. Per
//                     earlier spec "kalau unavailable/soldout ikuti native".
//
// Why we manage .disabled (instead of delegating to native): native
// updateCartButton (theme.js:4002) filters cross-narrow with `if(ao !== opt)`
// — a VALUE comparison without position check. natalie-rolt has Color and
// Undergarment that share values (White, Ice Blue, Espresso), so when
// Color="Espresso" is selected and (Size, Espresso, Espresso) variants are
// unavailable, native spuriously skips adding .disabled to UG="Espresso"
// because the value matches opt. Result: UG with same value as current Color
// looks "brighter" while other UG values look gray. Bug surfaced 2026-06-16.
//
// Auto-fallback (mirror style-with.js:209-215): if the :checked UG becomes
// nonexistent after re-narrowing, shift to first selectable + dispatch
// change so master <select> + native variant.id re-sync.
//
// Direction: unidirectional Color × Size → Undergarment. Reads variants from
// <script type="application/json" data-product-variants> injected by
// snippets/product-template.liquid.
// =============================================================================
(function UndergarmentNarrow() {
  const FORM_SEL = 'form.product-single__form';
  const VARIANTS_JSON_SEL = 'script[type="application/json"][data-product-variants]';
  // Accepted option names for UG fieldset (case-insensitive). "Lace Set" is the
  // current UX label (2026-06-30 pivot); "undergarment" kept for back-compat
  // with legacy products that still use the original name.
  const UG_OPTION_NAMES = ['lace set', 'undergarment'];
  const isUgName = (name) => UG_OPTION_NAMES.includes((name || '').toLowerCase().trim());

  function getVariants() {
    const node = document.querySelector(VARIANTS_JSON_SEL);
    if (!node) return [];
    try { return JSON.parse(node.textContent || '[]'); } catch { return []; }
  }

  function readOptionPositions(form) {
    // { optionNameLower: 0-based position } derived from data-index="optionN"
    const map = {};
    form.querySelectorAll('fieldset.variant-input-wrap[name]').forEach((fs) => {
      const name = (fs.getAttribute('name') || '').toLowerCase().trim();
      const dataIdx = fs.getAttribute('data-index') || '';
      const m = dataIdx.match(/option(\d+)/);
      if (name && m) map[name] = parseInt(m[1], 10) - 1;
    });
    return map;
  }

  function readCurrentSelections(form) {
    const sels = {};
    form.querySelectorAll('fieldset.variant-input-wrap[name]').forEach((fs) => {
      const name = (fs.getAttribute('name') || '').toLowerCase().trim();
      const r = fs.querySelector('input[type="radio"]:checked');
      if (name) sels[name] = r ? r.value : null;
    });
    return sels;
  }

  function findUndergarmentFieldset(form) {
    return form.querySelector('fieldset[data-undergarment-fieldset]')
        || Array.from(form.querySelectorAll('fieldset.variant-input-wrap[name]'))
             .find(fs => isUgName(fs.getAttribute('name')))
        || null;
  }

  function findUgOptionPosition(positions) {
    for (const name of UG_OPTION_NAMES) {
      if (typeof positions[name] === 'number') return positions[name];
    }
    return undefined;
  }

  function applyNarrowing() {
    const form = document.querySelector(FORM_SEL);
    if (!form) return;
    const variants = getVariants();
    if (!variants.length) return;

    const positions = readOptionPositions(form);
    const ugPos = findUgOptionPosition(positions);
    if (typeof ugPos !== 'number') return; // no UG option on this product

    const sels = readCurrentSelections(form);
    const ugFs = findUndergarmentFieldset(form);
    if (!ugFs) return;

    const optKey = (i) => `option${i + 1}`;

    // matchesCombo: does the variant satisfy (current selections × this ugValue)?
    const buildComboMatcher = (ugValue) => (v) => {
      if (v[optKey(ugPos)] !== ugValue) return false;
      for (const [name, pos] of Object.entries(positions)) {
        if (isUgName(name)) continue;
        const expected = sels[name];
        if (expected && v[optKey(pos)] !== expected) return false;
      }
      return true;
    };

    const ugInputs = Array.from(ugFs.querySelectorAll('input[type="radio"]'));

    ugInputs.forEach((input) => {
      const ugValue = input.value;
      const matchesCombo = buildComboMatcher(ugValue);
      const exists = variants.some(matchesCombo);
      const available = exists && variants.some((v) => v.available && matchesCombo(v));
      const label = ugFs.querySelector(`label[for="${input.id}"]`);

      // Nonexistent: block selection via HTML disabled attr + .is-nonexistent
      input.disabled = !exists;
      for (const el of [input, label]) {
        if (!el) continue;
        el.classList.toggle('is-nonexistent', !exists);
      }

      // Sold-out: manage .disabled ourselves (override native's buggy
      // ao !== opt cross-narrow filter). queueMicrotask in init() ensures
      // our pass runs AFTER native updateCartButton on the same change tick,
      // so our final state wins.
      if (label) {
        if (exists && !available) {
          label.classList.add('disabled');
        } else if (exists && available) {
          label.classList.remove('disabled');
        }
        // !exists case: .is-nonexistent visual covers it; .disabled left as-is
      }
    });

    // Auto-fallback: if the currently :checked Undergarment is now disabled
    // (combo became nonexistent after Color/Size change), shift to the first
    // selectable Undergarment and dispatch change so native variant.js,
    // updateCartButton, etc. re-sync against the new selection. Without this,
    // the radio shows the user's prior selection but <select>.selectedIndex
    // points elsewhere (set by custom.js:363+ which only matches Size/Color),
    // and clicking ATC would add a variant that doesn't match the visible UG.
    const checkedInput = ugInputs.find((i) => i.checked);
    if (checkedInput && checkedInput.disabled) {
      const fallback = ugInputs.find((i) => !i.disabled);
      if (fallback) {
        fallback.checked = true;
        fallback.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  // Update the "Lace Set — {selected}" info span in the fieldset label.
  // Mirror v2 picker pattern (matching-undergarment.js selectSwatch).
  // No-op kalau info span tidak dirender (Liquid conditional fieldset_has_swatch
  // = false → snippet skipped info span entirely). Sync ke selection terbaru
  // setelah auto-fallback (selection bisa berubah saat applyNarrowing dispatch
  // change), jadi panggil setelah applyNarrowing di event handler.
  function updateUndergarmentLabel() {
    const form = document.querySelector(FORM_SEL);
    if (!form) return;
    const ugFs = findUndergarmentFieldset(form);
    if (!ugFs) return;
    const lbl = document.querySelector('[data-undergarment-selected-label]');
    if (!lbl) return; // info span tidak dirender (text-button mode), no-op
    const checked = ugFs.querySelector('input[type="radio"]:checked');
    if (checked) lbl.textContent = checked.value;
  }

  function applyAll() {
    applyNarrowing();
    updateUndergarmentLabel();
  }

  function init() {
    const form = document.querySelector(FORM_SEL);
    if (!form) return;
    form.addEventListener('change', () => queueMicrotask(applyAll));
    document.addEventListener('shopify:section:load', () => queueMicrotask(applyAll));
    applyAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
